<template>
    <section class="m-auto px-2 md:px-0 text-secondary" :class="classToUse">
        <div class="rounded shadow p-3 bg-white pb-10 text-left">
            <img src="@/assets/mp-logo.webp" class="w-1/3 mr-auto pt-2 pl-2">
            <LoaderComponent v-if="!orderDataObtained" />
            <p class="text-2xl font-bold py-4" v-if="orderDataObtained">
                {{ getTitle() }}
            </p>
            <p v-if="orderDataObtained">
                {{ getMessage() }}
            </p>
            <div class="mt-10 border-t border-iron pt-3 text-left" v-if="orderDataObtained && orderData">
                <p class="mb-2">Total:</p>
                <p class="font-bold text-2xl">{{ orderData.total_paid ?? orderData.preference_data.formatedPrice ?? 'R$ 0.00' }}</p>
                <div class="mt-2 border-t border-iron">
                    <p class="pt-3 pb-5">
                        {{ translate('Purchase information:') }}
                    </p>
                    <div v-for="(item, index) of orderData.items" :key="index" class="flex mb-3">
                        <i class="pi pi-circle-fill my-auto"></i>
                        <p class="ml-2">{{ item.title }}</p>
                    </div>
                </div>
                <div class="pt-3 pb-5 border-t border-iron flex">
                    <span class="mr-2 mt-auto" v-if="orderData.payment_id">
                        {{ translate('Transaction number:') }}
                    </span>
                    <span class="text-xl" v-if="orderData.payment_id">
                        {{ orderData.payment_id ?? '' }}
                    </span>
                </div>

                <div class="mt-10 text-center">
                    <div 
                        v-if="orderData.payment_id && paymentApproved()"
                        class="bg-mp-blue py-2 px-2 rounded-lg shadow text-white w-full md:w-3/4 cursor-pointer flex justify-center mx-auto"
                        @click.prevent="openReceiptOnMercadoPago()"
                    >
                        <img class="mr-2" width="30" src="@/assets/mp_favicon.svg">
                        <span class="my-auto text-lg">
                            {{ translate('view receipt', 'upper') }}
                        </span>
                    </div>
                    <div v-if="!paymentApproved()" class="text-left">
                        <p class="mb-5 text-lg" v-html="getPendingMessage()"></p>

                        <!-- <div 
                            :class="getPendingAction().classStyle" class="py-2 px-2 rounded-lg shadow text-white w-full md:w-3/4 cursor-pointer flex justify-center mx-auto"
                            @click.prevent="callAction(getPendingAction().action)"
                        >
                            <img class="mr-2" width="30" src="@/assets/mp_favicon.svg" v-if="getPendingAction().valid">
                            <i v-if="!getPendingAction().valid" :class="getPendingAction().icon" class="mr-2 my-auto"></i>
                            <span class="my-auto text-lg">{{ getPendingAction().message }}</span>
                        </div> -->
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
<script setup>
    import { isScreeSmall } from '@/composables/windowSize';
    import { onUnmounted, onMounted, ref } from 'vue';
    import LoaderComponent from '@/components/Utils/LoaderComponent.vue';
    import paymentService from '@/services/PaymentService';
    import { useRoute } from 'vue-router';
    import { translate } from '@/composables/translate';

    const route = useRoute();
    const orderDataObtained = ref(false);
    const orderData = ref({});
    const type = ref('success');
    const breakPoint = 500;
    onMounted(() => {
        window.addEventListener('resize', updateScreenSize);
        updateScreenSize();

        getOrderData();
    });

    onUnmounted(() => {
      window.removeEventListener('resize', updateScreenSize);
    });

    const classToUse = ref('');
    const screenSize = ref(window.innerWidth);
    const updateScreenSize = () => {
        screenSize.value = window.innerWidth;
        classToUse.value = isScreeSmall(breakPoint) ? 'w-full' : 'm-form-w';
    };

    const messages = {
        approved: {
            title: 'Payment successful!',
            message: 'Mercado Pago has processed your payment. Your course will be available in your members area shortly.',
            btn_main: 'VIEW RECEIPT'
        },
        created: {
            title: 'Awaiting payment',
            message: 'Your order has been created and we are awaiting payment confirmation from Mercado Pago.',
            valid: 'You can still complete your payment by clicking the button below:',
            not_valid: 'This payment link is no longer valid.',
            not_valid_2: 'To proceed, you can generate a new payment link:',
            btn_valid: 'PAY NOW',
            btn_not_valid: 'RETRY PAYMENT'
        },
        expired: {
            title: 'Payment expired',
            message: 'The time limit to complete this payment has run out.',
            not_valid: 'We could not verify the payment within the required time limit.',
            not_valid_2: "But don't worry! You can generate a new payment link right now:",
            btn_not_valid: 'GENERATE NEW LINK'
        },
        refused: {
            title: 'Payment declined',
            message: 'There was an issue processing your payment with the card issuer.',
            not_valid: 'The payment was not authorized by Mercado Pago.',
            not_valid_2: 'You can try again by generating a new link:',
            btn_not_valid: 'TRY AGAIN'
        },
        no_status: {
            title: 'Unknown status',
            message: 'We could not determine the current status of this order. Please check your email or contact support.'
        }
    };

    function getTitle() {
        return translate(messages[type.value]?.title || messages.no_status.title);
    }

    function getMessage() {
        return translate(messages[type.value]?.message || messages.no_status.message);
    }

    function getPendingMessage() {
        const msgSource = messages[type.value] || messages.created;
        if (orderData.value.valid_link) {
            return translate(msgSource.valid || messages.created.valid);
        } 

        const part1 = translate(msgSource.not_valid || messages.created.not_valid);
        const part2 = translate(msgSource.not_valid_2 || messages.created.not_valid_2);
        return `${part1} <br> ${part2}`;
    }

    async function getOrderData() {
        const routeParams = route.query;
        let externalReference = routeParams.payment_id ?? '';
        
        let payment = null;
        if (externalReference) {
            payment = await paymentService.getPayment(externalReference);
        }

        orderData.value = payment;
        type.value = payment ? payment.mapStatus() : 'no_status';
        orderDataObtained.value = true;
    }

    function openReceiptOnMercadoPago() {
        paymentService.openMercadoPagoReceiptPage(orderData.value.payment_id);
    }

    function paymentApproved() {
        return ['approved'].includes(type.value);
    }
</script>